import React, { useRef, useState } from "react";
import { useUrlFriendlyToParameters } from 'helpers/urlFriendly';
import { useQuery } from "@tanstack/react-query";
import { getMedallists } from "data/Querys";
import Loader from "components/general/Loader";
import NotAvailable from "components/common/NotAvailable";
import MedallistsTable from "components/common/medals/MedallistsTable";
import SelectorDiscipline from "components/common/selectors/selector-discipline/selectorDiscipline";
import SelectorEvent from "components/common/selectors/SelectorEvent";
import { Stack } from "react-bootstrap";
import SelectorNoc from "components/common/selectors/selector-noc";
import { ALL_URL, SELECTOR_DISPLAY_TYPE, groupByDate } from "helpers/utils";
import SelectorDay from "components/common/selector-day/SelectorDay";
import { useLabels } from "components/general/Label";
import SelectorGender from "components/common/selectors/selector-gender";
import { ResetFilterButton } from "components/common/ResetFilterButton";

const Medallists = () => {
  const [dataState, setDataState] = useState([]);
  const [competitorFilter, setcompetitorFilter] = useState();
  const [itemDates, setItemDates] = useState({ competition_schedule: [] })
  const [disciplines, setDisciplines] = useState([]);
  const [nocs, setNocs] = useState([]);
  const [genders, setGenders] = useState([]);
  const [events, setEvents] = useState([]);
  const fLabel = useLabels()
  const header = fLabel({ data: 'LabelMedallists' });
  const placeholder = fLabel({ data: 'LabelCompetitor' });
  const { mirs_competition, mirs_language, mirs_discipline, mirs_event, mirs_date } = useUrlFriendlyToParameters();
  const inputRef = useRef(null);
  const [selectedNoc, setSelectedNoc] = useState('');
  const [selectedGender, setSelectedGender] = useState('');

  const { data, isError, isLoading } = useQuery(
    {
      queryKey: ['medallists', mirs_competition, mirs_language],
      queryFn: getMedallists,
      refetchInterval: 60000,
      //enabled: !!confCategory
    });

  React.useEffect(() => {
    let filterData = data?.medallist

    let tempDates = new Set();
    let tempDisciplines = new Set();
    let tempEvents = new Set();
    let tempNocs = new Set();
    let tempGenders = new Set();

    filterData?.forEach(dataItem => {
      let tempDate = { medal_date: dataItem?.medal_date };
      let tempDiscipline = dataItem?.discipline?.code;
      let tempNoc = dataItem?.participant?.organisation?.code;
      let tempGender = dataItem?.medal_sex;
      let tempEvent = dataItem?.event_code;

      if (mirs_date && mirs_date !== ALL_URL.ALL_DAYS) {
        if (dataItem?.medal_date !== mirs_date) {
          tempDiscipline = "";
          tempNoc = "";
          tempGender = ""
          tempEvent = "";
        };
      };

      if (mirs_discipline && mirs_discipline !== ALL_URL.ALL_DISC) {
        if (dataItem?.discipline?.code !== mirs_discipline) {
          tempDate = "";
          tempNoc = "";
          tempGender = "";
          tempEvent = "";
        };

        if (mirs_event && mirs_event !== ALL_URL.ALL_EVENT) {
          if (dataItem?.event_code !== mirs_event) {
            // clear date
            tempDate = "";
            // clear disc
            // tempDiscipline = "";
            // clear noc
            tempNoc = "";
            // clear sex
            tempGender = ""
          }
        }
      }

      if (selectedNoc && selectedNoc !== ALL_URL.ALL_NOC) {
        if (dataItem?.participant?.organisation?.code !== selectedNoc) {
          tempDate = "";
          tempDiscipline = "";
          tempGender = "";
          tempEvent = "";
        };
      };

      if (selectedGender && selectedGender !== "TOT") {
        if (dataItem?.medal_sex !== selectedGender) {
          tempDate = "";
          tempDiscipline = "";
          tempNoc = "";
          tempEvent = "";
        };
      };

      if (tempDate) {
        tempDates.add(dataItem);
      }
      if (tempNoc) {
        tempNocs.add(tempNoc);
      }
      if (tempDiscipline) {
        tempDisciplines.add(tempDiscipline);
      }
      if (tempGender) {
        tempGenders.add(tempGender);
      }
      if (tempEvent) {
        tempEvents.add(tempEvent);
      }
    });


    let tempDatesItems = groupByDate([...tempDates], "medal_date");
    let tempItemDates = { competition_schedule: [] };
    for (const [key, value] of Object.entries(tempDatesItems)) {
      tempItemDates.competition_schedule.push({ date: key })
    }
    setItemDates(tempItemDates);
    setDisciplines([...tempDisciplines]);
    setNocs([...tempNocs]);
    setGenders([...tempGenders]);
    setEvents([...tempEvents])

    if (mirs_discipline && mirs_discipline !== ALL_URL.ALL_DISC) {
      filterData = filterData?.filter(item => item?.discipline?.code === mirs_discipline)
      if (mirs_event && mirs_event !== ALL_URL.ALL_EVENT)
        filterData = filterData?.filter(item => item?.event_code === mirs_event)
    }
    if (selectedNoc && selectedNoc !== ALL_URL.ALL_NOC) {
      filterData = filterData?.filter(item => item?.participant?.organisation?.code === selectedNoc);
    }

    if (mirs_date && mirs_date !== ALL_URL.ALL_DAYS) {
      filterData = filterData?.filter(item => item?.medal_date === mirs_date)
    }

    if (selectedGender && selectedGender !== "TOT") {
      filterData = filterData?.filter(item => item?.medal_sex === selectedGender);
    }
    // spread athletes in each noc for teams and couple
    const newData = [];
    filterData?.forEach(d => {
      d?.athletes.forEach(ath => {
        if (!competitorFilter || (competitorFilter && ath?.athlete?.name?.toLowerCase().includes(competitorFilter.toLowerCase()))) {
          const item = { ...d, participant: { ...ath?.athlete, athleteEventCode: `${ath?.athlete?.code}-${d?.eventUnit?.code}` } };
          newData.push(item)
        }
        // create athleteEventCode to be used in mirstable as a keyRow, as there may be many rows with the same participant code
      })
    });

    setDataState(newData);
  }, [data, mirs_discipline, mirs_event, selectedNoc, mirs_date, competitorFilter, selectedGender]);

  const onSelectNoc = (nocCode) => {
    setSelectedNoc(nocCode)
  }
  const onSelectGender = (genderCode) => {
    setSelectedGender(genderCode)
  }

  const handleOnChange = event => {
    const { name, value } = event.target;
    //et filterData = dataState
    //console.log(filterData)
    // filterData = filterData?.filter(item => item?.participant?.name?.toLowerCase().includes(value.toLowerCase()));
    setcompetitorFilter(value)
  };

  const clearFilterText = () => {
    if (inputRef?.current?.value) {
      inputRef.current.value = "";
    }
    setcompetitorFilter("");
  };

  const onClearFilters = () => {
    setSelectedNoc('');
    setSelectedGender('');
    clearFilterText();
  };

  if (isError) { return <NotAvailable label="LabelNoDataAvailable" />; }
  if (isLoading) { return <Loader />; }
  if (!data?.medallist) { return <NotAvailable label="LabelNoDataAvailable" /> }
  return (<>
    <div className='mirs-page-container'>
      <Stack direction="horizontal" className='selectors-container'>
        <SelectorDay daysItems={itemDates} page="medallists" />
        <SelectorDiscipline display_type={SELECTOR_DISPLAY_TYPE.COMBO} page="medallists" disciplinesList={disciplines} useDisciplinesList={true} />
        <SelectorEvent page="medallists" eventList={events} useEventList={true} />
        <SelectorNoc page="medallists" nocsList={nocs} useNocList={true} onSelectNoc={onSelectNoc} selectedNoc={selectedNoc} />
        <SelectorGender page="medallists" gendersList={genders} useGendersList={true} onSelectGender={onSelectGender} selectedGender={selectedGender} />
        <div className="d-flex competitor-search">
          <input type="text" className="form-control" ref={inputRef} name="Competitor" id="Competitor"
            placeholder={placeholder}
            onChange={handleOnChange} />
        </div>
        <ResetFilterButton clearFilterText={clearFilterText} filterText={competitorFilter} page='medallists' selectedNoc={selectedNoc} selectedGender={selectedGender} onClearFilters={onClearFilters} />
      </Stack>

      {dataState && <MedallistsTable data={dataState} key={`medallists`} />}

    </div>
  </>
  )





};


export default Medallists

